import React, { useEffect, useState } from 'react';
import { RetailerType } from '@models/OpportunityReview';
import { useAnalytics } from '@ecosystem/newron-client-lib';
import { useSelector } from 'react-redux';
import {
  CHART_RADIO_BUTTON_TRACKING_TITLE,
  ChartElementName,
  ChartElementType,
  chartInteractionTrackingFunction,
  ChartInteractionTrackingFunctionParam,
} from '@utils/page-analytics/model/ChartInteractionTracking';
import { FactRevenue, getPageTypeWithPageName } from '@utils/pageConfig/Types';
import _ from 'lodash';
import { ElementType } from '@utils/page-analytics/model/AnalyticTrackingBasePropertiesObj';
import { OPPORTUNITY_REVIEW_CARD_TITLE } from '@pages/Utilities';
import { constructCurrencyLabel } from '@utils/currencyUtils/CurrencyUtils';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { WithHiddenListBox } from '@components/design';

interface Props {
  selectedRetailerType: RetailerType;
  onRetailerTypeChange: (retailerType: RetailerType) => void;
  factRevenue: FactRevenue;
  isDisabled: boolean;
}

export const MARKET_REVENUE = 'Market Revenue';
export const RETAILER_REVENUE = 'Retailer Revenue';
export const DATA_POINT_SIZE_BASED_ON = 'data point size based on';

const OpportunityReviewChartFilter: React.FC<Props> = ({
  selectedRetailerType,
  onRetailerTypeChange,
  factRevenue,
  isDisabled,
}) => {
  const { track } = useAnalytics();
  const pagesConfig = usePageConfig();
  const retailerClientState = useSelector((state: any) => state.retailerClient);
  const sideFiltersState = useSelector((state: any) => state.sideFilters);
  const currencyLabel = constructCurrencyLabel(factRevenue);
  const [dataPointValue, setDataPointValue] = useState<string | null>(selectedRetailerType);

  // Chart Interaction Tracking Function
  const handleChartInteractionTracking = (retailerType: string) => {
    const trackingParam: ChartInteractionTrackingFunctionParam = {
      eventTitle: CHART_RADIO_BUTTON_TRACKING_TITLE,
      sideFilters: sideFiltersState.appliedFilters,
      retailerClientState: retailerClientState,
      track: track,
      pageType: getPageTypeWithPageName(retailerClientState.pageViewing),
      pageName: retailerClientState.pageViewing,
      elementName: ChartElementName.DATA_POINT_SIZE,
      elementType: ChartElementType.FILTER,
      optionChosen: `${_.capitalize(retailerType)} Revenue`,
      chartName: `${OPPORTUNITY_REVIEW_CARD_TITLE} ${ElementType.BUBBLE_CHART}`,
      access: pagesConfig.access,
    };
    chartInteractionTrackingFunction(trackingParam);
  };
  const handleRetailerTypeChange = (retailerType: string) => {
    handleChartInteractionTracking(retailerType);
    onRetailerTypeChange(retailerType as RetailerType);
  };
  const options = [
    { label: `${MARKET_REVENUE}${currencyLabel}`, value: RetailerType.MARKET_REVENUE },
    { label: `${RETAILER_REVENUE}${currencyLabel}`, value: RetailerType.MY_REVENUE },
  ];

  const selectedValue = options.find((o) => o.value === selectedRetailerType)?.label ?? options[0].label;

  useEffect(() => {
    handleSalesTypeChange(dataPointValue ?? options[0].value);
  }, [dataPointValue]);

  const handleSalesTypeChange = (retailerType: string) => {
    onRetailerTypeChange(retailerType as RetailerType);
  };

  return (
    <div
      key={`${factRevenue}`} //NOSONAR typescript:S6551
      data-testid="show-selector"
    >
      <WithHiddenListBox
        isDisabled={isDisabled}
        options={options}
        label={DATA_POINT_SIZE_BASED_ON}
        value={selectedValue}
        onChange={(option) => {
          setDataPointValue(option.value);
          handleRetailerTypeChange(option.value);
        }}
      />
    </div>
  );
};

export default OpportunityReviewChartFilter;
